import { motion, AnimatePresence } from 'framer-motion';
import { X, ExternalLink, Sparkles, Target, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { rankAccent } from '../lib/rankColors';

type AnswerRow = { id: string; name: string; rank: number };

type Zone = {
  id: string;
  label: string;
  hint: string;
  from: number;
  to: number;
  accent: string;
  accentSoft: string;
};

const ZONES: Zone[] = [
  {
    id: 'obvious',
    label: 'The Obvious',
    hint: 'Everyone knows these',
    from: 1,
    to: 25,
    accent: '#fb923c',
    accentSoft: 'rgba(251,146,60,0.12)',
  },
  {
    id: 'familiar',
    label: 'The Familiar',
    hint: 'Pretty popular',
    from: 26,
    to: 50,
    accent: '#facc15',
    accentSoft: 'rgba(250,204,21,0.12)',
  },
  {
    id: 'solid',
    label: 'Solid Territory',
    hint: 'Worth real points',
    from: 51,
    to: 75,
    accent: '#4ade80',
    accentSoft: 'rgba(74,222,128,0.12)',
  },
  {
    id: 'deep',
    label: 'Deep Cuts',
    hint: 'Hidden gems near #100',
    from: 76,
    to: 100,
    accent: '#22d3ee',
    accentSoft: 'rgba(34,211,238,0.14)',
  },
];

export function AnswerListModal({
  title,
  topicSources,
  answers,
  highlightRanks,
  onClose,
  loading,
}: {
  title: string;
  topicSources?: {
    source_1?: string | null;
    source_2?: string | null;
    source_3?: string | null;
  };
  answers: AnswerRow[];
  highlightRanks: number[];
  loading: boolean;
  onClose: () => void;
}) {
  const [search, setSearch] = useState('');
  const highlightSet = useMemo(() => new Set(highlightRanks), [highlightRanks]);
  const foundCount = highlightRanks.length;

  const sorted = useMemo(
    () => [...answers].sort((a, b) => a.rank - b.rank),
    [answers]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return sorted;
    const rankQuery = q.replace(/^#/, '');
    const wantsRank = /^\d{1,3}$/.test(rankQuery);
    return sorted.filter((a) => {
      if (a.name.toLowerCase().includes(q)) return true;
      if (wantsRank && String(a.rank) === rankQuery) return true;
      return false;
    });
  }, [sorted, search]);

  const byZone = useMemo(() => {
    return ZONES.map((zone) => ({
      zone,
      items: filtered.filter((a) => a.rank >= zone.from && a.rank <= zone.to),
      found: filtered.filter(
        (a) => a.rank >= zone.from && a.rank <= zone.to && highlightSet.has(a.rank)
      ).length,
    }));
  }, [filtered, highlightSet]);

  const isSearching = search.trim().length > 0;
  const matchCount = filtered.length;

  const hasSources = Boolean(
    topicSources && (topicSources.source_1 || topicSources.source_2 || topicSources.source_3)
  );

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[60] flex items-center justify-center p-3 sm:p-4 bg-background/80 backdrop-blur-sm"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 16 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96, y: 16 }}
          transition={{ type: 'spring', stiffness: 260, damping: 22 }}
          className="w-full max-w-3xl bg-background border-2 border-border rounded-3xl shadow-2xl relative max-h-[90vh] overflow-hidden flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Hero header */}
          <div className="relative shrink-0 border-b border-border overflow-hidden">
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 opacity-[0.35] dark:opacity-[0.22]"
              style={{
                background:
                  'radial-gradient(ellipse 80% 120% at 10% -20%, rgba(251,146,60,0.35), transparent 55%), radial-gradient(ellipse 70% 100% at 90% 0%, rgba(34,211,238,0.28), transparent 50%)',
              }}
            />
            <div className="relative px-5 sm:px-6 pt-5 pb-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.2em] text-foreground/45 mb-1.5">
                    <Sparkles size={12} />
                    The Full Top 100
                  </div>
                  <h2 className="text-xl sm:text-2xl font-black tracking-tight leading-tight">
                    {title}
                  </h2>
                </div>
                <button
                  onClick={onClose}
                  className="shrink-0 p-2 rounded-full hover:bg-muted transition-colors"
                  aria-label="Close"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Spectrum — same language as How to Play */}
              <div className="mt-4">
                <div className="h-1.5 rounded-full bg-[linear-gradient(to_right,#fb923c,#facc15,#4ade80,#22d3ee)]" />
                <div className="flex justify-between mt-1.5 text-[9px] font-black uppercase tracking-widest text-foreground/35">
                  <span>#1 Obvious</span>
                  <span className="text-foreground/70">#100 Deep cut</span>
                </div>
              </div>

              {/* Your finds */}
              {!loading && foundCount > 0 && (
                <div className="mt-3.5 flex items-center gap-2 flex-wrap">
                  <div className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-foreground/50">
                    <Target size={12} />
                    You found
                  </div>
                  {[...highlightRanks]
                    .sort((a, b) => a - b)
                    .map((rank) => (
                      <span
                        key={rank}
                        className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-black tabular-nums ${
                          rank === 100
                            ? 'text-black bg-[linear-gradient(to_right,#CD7F32,#a1a1aa,#facc15,#22d3ee)]'
                            : 'text-background'
                        }`}
                        style={rank === 100 ? undefined : { backgroundColor: rankAccent(rank) }}
                      >
                        #{rank}
                      </span>
                    ))}
                </div>
              )}

            </div>
          </div>

          {/* Scrollable body */}
          <div className="flex-1 overflow-y-auto overscroll-contain px-4 sm:px-5 py-4">
            {loading ? (
              <div className="py-20 text-center text-sm text-foreground/60 animate-pulse">
                Unveiling the Top 100…
              </div>
            ) : (
              <div className="space-y-7">
                {hasSources && topicSources && (
                  <div className="rounded-2xl border border-border bg-muted/30 px-4 py-3.5">
                    <div className="text-[10px] font-black uppercase tracking-widest text-foreground/45 mb-2.5 flex items-center gap-1.5">
                      <ExternalLink size={12} />
                      Sources
                    </div>
                    <div className="flex flex-wrap gap-2 mb-2.5">
                      {[topicSources.source_1, topicSources.source_2, topicSources.source_3]
                        .filter(Boolean)
                        .map((href, i) => (
                          <a
                            key={String(href)}
                            href={href!}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs font-semibold bg-background border border-border px-3 py-1.5 rounded-lg hover:bg-muted transition-colors"
                          >
                            Source {i + 1}
                          </a>
                        ))}
                    </div>
                    <p className="text-[11px] text-foreground/45 leading-relaxed">
                      Sources are the foundation — final rankings may include small adjustments for balance and playability.
                    </p>
                  </div>
                )}

                {/* Search — sits right above the list so it's hard to miss */}
                <div className="space-y-2">
                  <div className="relative">
                    <Search
                      size={16}
                      className="absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground/40 pointer-events-none"
                    />
                    <input
                      type="search"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search the Top 100…"
                      className="w-full rounded-2xl border-2 border-border bg-muted/40 pl-10 pr-11 py-3 text-sm font-semibold placeholder:text-foreground/40 focus:outline-none focus:border-foreground/30 focus:bg-background transition-colors"
                      autoComplete="off"
                      spellCheck={false}
                    />
                    {isSearching && (
                      <button
                        type="button"
                        onClick={() => setSearch('')}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1.5 rounded-full hover:bg-muted text-foreground/50"
                        aria-label="Clear search"
                      >
                        <X size={15} />
                      </button>
                    )}
                  </div>
                  {isSearching && (
                    <div className="text-[11px] font-bold uppercase tracking-widest text-foreground/40 px-0.5">
                      {matchCount === 0
                        ? 'No matches'
                        : `${matchCount} match${matchCount === 1 ? '' : 'es'}`}
                    </div>
                  )}
                </div>

                {isSearching && matchCount === 0 && (
                  <div className="rounded-2xl border border-dashed border-border py-12 px-4 text-center">
                    <div className="text-sm font-semibold text-foreground/70">
                      Nothing named “{search.trim()}”
                    </div>
                    <div className="text-[11px] text-foreground/40 mt-1">
                      Not on this Top 100 — or try a different spelling.
                    </div>
                  </div>
                )}

                {byZone.map(({ zone, items, found }, zoneIndex) => {
                  if (items.length === 0) return null;
                  return (
                    <motion.section
                      key={zone.id}
                      initial={isSearching ? false : { opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: isSearching ? 0 : zoneIndex * 0.06, duration: 0.35 }}
                    >
                      <div className="flex items-end justify-between gap-3 mb-2.5 px-0.5">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span
                              className="h-2 w-2 rounded-full shrink-0"
                              style={{ backgroundColor: zone.accent }}
                            />
                            <h3 className="text-sm font-black tracking-tight">{zone.label}</h3>
                            <span className="text-[10px] font-bold tabular-nums text-foreground/35">
                              #{zone.from}–#{zone.to}
                            </span>
                          </div>
                          <p className="text-[11px] text-foreground/45 mt-0.5 ml-4">{zone.hint}</p>
                        </div>
                        {found > 0 && (
                          <div
                            className="shrink-0 text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded-md"
                            style={{ backgroundColor: zone.accentSoft, color: zone.accent }}
                          >
                            {found} found
                          </div>
                        )}
                      </div>

                      <div className="rounded-2xl border border-border overflow-hidden bg-background">
                        {items.map((a, i) => {
                          const isHighlighted = highlightSet.has(a.rank);
                          const accent = rankAccent(a.rank);
                          const isCentumth = a.rank === 100;
                          return (
                            <div
                              key={a.id}
                              className={`relative flex items-stretch gap-0 border-b border-border last:border-b-0 transition-colors ${
                                isHighlighted
                                  ? 'bg-foreground text-background'
                                  : 'hover:bg-muted/50'
                              }`}
                            >
                              {/* Rank depth rail */}
                              <div
                                className="w-1 shrink-0"
                                style={{
                                  backgroundColor: isHighlighted ? 'rgba(255,255,255,0.35)' : accent,
                                }}
                              />

                              <div className="flex items-start gap-3 flex-1 min-w-0 px-3 sm:px-3.5 py-2.5">
                                <div
                                  className={`shrink-0 font-mono tabular-nums text-xs sm:text-sm font-black px-2 py-1 rounded-md leading-none mt-0.5 ${
                                    isHighlighted
                                      ? 'bg-background/20 text-background'
                                      : 'text-background'
                                  }`}
                                  style={
                                    isHighlighted
                                      ? undefined
                                      : { backgroundColor: accent }
                                  }
                                >
                                  #{a.rank}
                                </div>

                                <div className="min-w-0 flex-1 pt-0.5">
                                  <div
                                    className={`font-semibold leading-snug break-words ${
                                      isCentumth && !isHighlighted ? 'tracking-tight' : ''
                                    }`}
                                  >
                                    {a.name}
                                    {isCentumth && (
                                      <span className="ml-2 inline-flex align-middle text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded text-black bg-[linear-gradient(to_right,#CD7F32,#a1a1aa,#facc15,#22d3ee)]">
                                        Centumth
                                      </span>
                                    )}
                                  </div>
                                </div>

                                {isHighlighted && (
                                  <div className="shrink-0 self-center text-[9px] font-black uppercase tracking-widest opacity-80 whitespace-nowrap">
                                    Yours
                                  </div>
                                )}
                              </div>

                              {/* Subtle index fade for rhythm */}
                              {!isHighlighted && i % 5 === 4 && (
                                <div
                                  aria-hidden
                                  className="pointer-events-none absolute inset-x-0 bottom-0 h-px opacity-40"
                                  style={{
                                    background: `linear-gradient(to right, transparent, ${accent}, transparent)`,
                                  }}
                                />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </motion.section>
                  );
                })}

                {!isSearching && (
                  <div className="pt-1 pb-3 text-center text-[11px] text-foreground/35 font-medium tracking-wide">
                    That’s the full Top 100 — see you tomorrow.
                  </div>
                )}
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
